import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, TradingConfig, TradingPair, PortfolioData } from '@/types';
import { api } from './api';

interface AuthStore {
  isAuthenticated: boolean;
  user: User | null;
  sessionToken: string | null;
  isLoading: boolean;
  error: string | null;

  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      sessionToken: null,
      isLoading: true,
      error: null,

      setAuth: (user, token) => {
        console.log('[Auth] setAuth called with user:', user?.userId, 'token:', token ? token.substring(0, 8) + '...' : 'null');
        api.setToken(token);
        set({ isAuthenticated: true, user, sessionToken: token, error: null });
        console.log('[Auth] Auth state set successfully');
      },

      clearAuth: () => {
        api.setToken(null);
        set({ isAuthenticated: false, user: null, sessionToken: null });
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      checkAuth: async () => {
        const { sessionToken } = get();
        console.log('[Auth] checkAuth called, token exists:', !!sessionToken);
        if (!sessionToken) {
          console.log('[Auth] No session token, skipping auth check');
          set({ isLoading: false });
          return;
        }

        api.setToken(sessionToken);
        try {
          console.log('[Auth] Validating existing session...');
          const { user } = await api.getCurrentUser();
          console.log('[Auth] Session valid, user:', user?.userId);
          set({ isAuthenticated: true, user, isLoading: false });
        } catch (error) {
          // Silently clear stale/invalid session - don't show error to user
          // This handles expired sessions gracefully without confusing "Unauthorized" message
          console.log('[Auth] Session invalid, clearing auth. Error:', error instanceof Error ? error.message : error);
          get().clearAuth();
          set({ isLoading: false, error: null });
        }
      },
    }),
    {
      name: 'lazaitrader-auth',
      partialize: (state) => ({
        sessionToken: state.sessionToken,
      }),
    }
  )
);

interface DataStore {
  configs: TradingConfig[];
  pairs: TradingPair[];
  portfolio: PortfolioData | null;
  isLoadingConfigs: boolean;
  isLoadingPairs: boolean;
  isLoadingPortfolio: boolean;

  fetchConfigs: () => Promise<void>;
  fetchPairs: () => Promise<void>;
  fetchPortfolio: () => Promise<void>;
  addConfig: (config: TradingConfig) => void;
  updateConfig: (configId: number, updates: Partial<TradingConfig>) => void;
  removeConfig: (configId: number) => void;
  reset: () => void;
}

export const useDataStore = create<DataStore>((set, get) => ({
  configs: [],
  pairs: [],
  portfolio: null,
  isLoadingConfigs: false,
  isLoadingPairs: false,
  isLoadingPortfolio: false,

  fetchConfigs: async () => {
    set({ isLoadingConfigs: true });
    try {
      const { configs } = await api.getConfigs();
      set({ configs, isLoadingConfigs: false });
    } catch (error) {
      console.error('Failed to fetch configs:', error);
      set({ isLoadingConfigs: false });
    }
  },

  fetchPairs: async () => {
    set({ isLoadingPairs: true });
    try {
      const { pairs } = await api.getTradingPairs();
      set({ pairs, isLoadingPairs: false });
    } catch (error) {
      console.error('Failed to fetch pairs:', error);
      set({ isLoadingPairs: false });
    }
  },

  fetchPortfolio: async () => {
    set({ isLoadingPortfolio: true });
    try {
      const portfolio = await api.getPortfolio();
      set({ portfolio, isLoadingPortfolio: false });
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
      set({ isLoadingPortfolio: false });
    }
  },

  addConfig: (config) => {
    set({ configs: [...get().configs, config] });
  },

  updateConfig: (configId, updates) => {
    set({
      configs: get().configs.map((c) =>
        c.configId === configId ? { ...c, ...updates } : c
      ),
    });
  },

  removeConfig: (configId) => {
    set({
      configs: get().configs.filter((c) => c.configId !== configId),
    });
  },

  reset: () => {
    set({
      configs: [],
      pairs: [],
      portfolio: null,
    });
  },
}));
